<template>
  <div class="prod-card" @click="$emit('open')">
    <div class="prod-card__media">
      <img v-if="image" :src="image" :alt="title" loading="lazy" />
      <div v-else class="prod-card__media--empty" :style="{ background: accent }">
        <i class="pi" :class="kind === 'course' ? 'pi-book' : 'pi-graduation-cap'"></i>
      </div>
      <span v-if="isOwnYear" class="prod-card__own"><i class="pi pi-star-fill"></i> سنتك الدراسية</span>
      <span v-else-if="featured" class="prod-card__featured"><i class="pi pi-star-fill"></i> مميز</span>
    </div>

    <div class="prod-card__body">
      <h3 class="prod-card__title">{{ title }}</h3>
      <p v-if="teacher" class="prod-card__teacher"><i class="pi pi-user"></i> {{ teacher }}</p>
      <p v-if="product.description" class="prod-card__desc">{{ product.description }}</p>

      <div v-if="isFree" class="prod-card__free"><i class="pi pi-gift"></i> مجاني</div>
      <div class="prod-card__plans" v-else-if="plans.length">
        <span v-for="p in plans" :key="p.type" class="plan-chip">
          {{ p.label }}: <strong>{{ money(p.price) }}</strong>
        </span>
      </div>
      <p v-else class="prod-card__noplans">لا توجد خطط متاحة حالياً</p>
    </div>

    <div class="prod-card__footer">
      <button class="prod-card__btn" :class="{ 'prod-card__btn--access': hasAccess }"
              :disabled="!hasAccess && !isFree && !product.accepts_subscriptions"
              @click.stop="$emit('subscribe')">
        <i class="pi" :class="hasAccess ? 'pi-sign-in' : 'pi-bolt'"></i>
        {{ ctaLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import { productPlans, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'ProductCard',
  props: {
    product: { type: Object, required: true },
    kind: { type: String, default: 'course' }, // 'course' | 'academic_year'
  },
  emits: ['open', 'subscribe'],
  computed: {
    image() { return this.product.cover_image_link || this.product.image_link || null; },
    title() { return this.kind === 'course' ? this.product.title : this.product.name; },
    teacher() { return this.kind === 'course' ? this.product.teacher : null; },
    accent() { return this.product.color || '#051629'; },
    featured() { return !!this.product.featured; },
    isOwnYear() { return this.kind === 'academic_year' && !!this.product.is_students_year; },
    isFree() { return !this.product.is_subscription_enabled; },
    hasAccess() { return !!this.product.has_access; },
    plans() { return productPlans(this.product); },
    ctaLabel() {
      if (this.hasAccess) return 'ادخل الآن';
      if (this.isFree) return 'ابدأ الآن';
      return this.product.accepts_subscriptions ? 'اشترك الآن' : 'غير متاح';
    },
  },
  methods: { money: formatMoney },
};
</script>

<style scoped>
.prod-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid rgba(5, 22, 41,.1);
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  height: 100%;
}
.prod-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(5, 22, 41,.14);
  border-color: rgba(5, 22, 41,.3);
}
.prod-card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #f1f5f9;
}
.prod-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.prod-card__media--empty {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 2.4rem;
}
.prod-card__featured {
  position: absolute; top: 10px; inset-inline-start: 10px;
  background: rgba(255,255,255,.92); color: #b45309;
  font-size: .72rem; font-weight: 800; padding: .25rem .6rem; border-radius: 99px;
}
.prod-card__own {
  position: absolute; top: 10px; inset-inline-start: 10px;
  background: rgba(245,158,11,.95); color: #1f2937;
  font-size: .72rem; font-weight: 800; padding: .25rem .6rem; border-radius: 99px;
  display: inline-flex; align-items: center; gap: .3rem;
}
.prod-card__body { padding: 1rem 1.1rem .4rem; flex: 1; }
.prod-card__title { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: .35rem; }
.prod-card__teacher { font-size: .82rem; color: #051629; font-weight: 700; margin-bottom: .35rem; }
.prod-card__desc {
  font-size: .84rem; color: #6B7280; margin-bottom: .6rem; line-height: 1.6;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.prod-card__plans { display: flex; flex-wrap: wrap; gap: .35rem; margin-bottom: .3rem; }
.plan-chip {
  background: rgba(5, 22, 41,.07); color: #374151;
  border: 1px solid rgba(5, 22, 41,.14);
  border-radius: 8px; padding: .2rem .5rem; font-size: .74rem;
}
.plan-chip strong { color: #051629; }
.prod-card__noplans { font-size: .78rem; color: #94a3b8; }
.prod-card__free {
  display: inline-flex; align-items: center; gap: .35rem;
  background: rgba(34,197,94,.1); color: #16a34a;
  border-radius: 8px; padding: .3rem .6rem; font-size: .78rem; font-weight: 800; margin-bottom: .3rem;
}
.prod-card__footer { padding: .6rem 1.1rem 1.1rem; }
.prod-card__btn {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
  background: linear-gradient(90deg, #051629, #f7b424);
  color: #fff; border: none; border-radius: 20px;
  padding: .6rem 1rem; font-weight: 800; font-size: .9rem; cursor: pointer;
  transition: opacity .2s ease, transform .1s ease;
}
.prod-card__btn--access { background: linear-gradient(90deg, #22c55e, #16a34a); }
.prod-card__btn:hover:not(:disabled) { opacity: .92; }
.prod-card__btn:active:not(:disabled) { transform: scale(.98); }
.prod-card__btn:disabled { background: #cbd5e1; cursor: not-allowed; }
</style>
